import { useCallback, useEffect, useRef, useState } from 'react';

export type ExportStatus = 'pending' | 'done' | 'error';

export interface ExportEntry {
  status: ExportStatus;
  /** Present on 'done'. */
  message?: string;
  /** Present on 'error'. */
  error?: string;
}

export interface ExportActions {
  entry: (key: string) => ExportEntry | undefined;
  isPending: (key: string) => boolean;
  /**
   * Start the export identified by `key`. `task` performs the fetch+download and
   * resolves with a success message. Concurrent keys run independently; a second
   * call for a key already pending is a no-op (duplicate-click suppression).
   */
  run: (key: string, task: () => Promise<string>) => void;
}

/**
 * Per-key concurrent export state for the Manager view (#126). Each export (the
 * org export and each trader) has its own pending/done/error entry, so exports
 * run independently and one's failure never strands or clobbers another's.
 */
export function useExportActions(): ExportActions {
  const [entries, setEntries] = useState<Record<string, ExportEntry>>({});
  // Synchronous in-flight set: state updates are async, so two fast clicks could
  // both pass a state-based guard before the first re-render disables the button.
  // The ref blocks the duplicate immediately; `entries` drives the UI.
  const pendingRef = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  useEffect(() => {
    // Re-arm on every mount: StrictMode's dev double-mount runs the cleanup and
    // re-runs this effect on the SAME hook instance, so a cleanup-only effect
    // would leave the ref false forever and silently swallow every completion.
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const run = useCallback((key: string, task: () => Promise<string>) => {
    if (pendingRef.current.has(key)) return;
    pendingRef.current.add(key);
    setEntries((prev) => ({ ...prev, [key]: { status: 'pending' } }));

    void (async () => {
      try {
        const message = await task();
        if (mountedRef.current) {
          setEntries((prev) => ({ ...prev, [key]: { status: 'done', message } }));
        }
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Export failed';
        if (mountedRef.current) {
          setEntries((prev) => ({ ...prev, [key]: { status: 'error', error } }));
        }
      } finally {
        pendingRef.current.delete(key);
      }
    })();
  }, []);

  const entry = useCallback((key: string) => entries[key], [entries]);
  const isPending = useCallback((key: string) => entries[key]?.status === 'pending', [entries]);

  return { entry, isPending, run };
}
